import { useState, useEffect, useRef } from "react";
import { classNames } from "primereact/utils";
import styles from "./GroupClassWizard.module.scss";
import { useGroupClassWizard } from "./hooks";
import { PreReservationFormDialog } from "../../../common/preReservationForms/PreReservationFormDialog";
import { usePreReservationFormForEvent } from "../../../common/preReservationForms/usePreReservationFormForReservation";
import {
  buildClientFormContext,
  formatFormPrice,
} from "../../../common/preReservationForms/clientFormContext";
import { IPreReservationFormSubmission } from "../../../common/preReservationForms/models";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";
import { useAppContext } from "../../../AppContext";
import { applyStudioWizardAccent } from "../../../common/infrastructure/utilities/colorUtility";
import {
  routePaths,
  callPhoneNumber,
} from "../../../common/infrastructure/routes/routePaths";
import { useNavigate } from "react-router-dom";
import { useGoogleAuthReturn } from "../../../common/infrastructure/hooks/useGoogleAuthReturn";
import { showCustomConfirmDialog } from "../../../common/components/dialogs/AppConfirmDialog";
import {
  PrepaymentDialog,
  IPrepaymentDialogConfirmResult,
} from "../../../common/components/PrepaymentDialog/PrepaymentDialog";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";

// Reuse components from appointment wizard
import { WizardHeader } from "../appointment/components/WizardHeader/WizardHeader";
import { ProgressBar } from "../appointment/components/ProgressBar/ProgressBar";
import { WizardStep } from "../appointment/components/WizardStep/WizardStep";
import { WizardNavigation } from "../appointment/components/WizardNavigation/WizardNavigation";
import { ServicesSelectionStep } from "../appointment/components/ServicesSelectionStep/ServicesSelectionStep";
import { EmployeesSelectionStep } from "../appointment/components/EmployeesSelectionStep/EmployeesSelectionStep";
import { WizardLoadingScreen } from "../appointment/components/WizardLoadingScreen/WizardLoadingScreen";
import { useWizardAnimations } from "../appointment/hooks/useWizardAnimations";
import { DraggableBackButton } from "../../../common/components/layout/components/DraggableBackButton";

import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";

// Custom group class components
import { GroupClassDateTimeSelectionStep } from "./components/GroupClassDateTimeSelectionStep/GroupClassDateTimeSelectionStep";
import { GroupClassConfirmationStep } from "./components/GroupClassConfirmationStep/GroupClassConfirmationStep";
import { GroupClassSuccessStep } from "./components/GroupClassSuccessStep/GroupClassSuccessStep";
import { GroupClassLoginStep } from "./components/GroupClassLoginStep/GroupClassLoginStep";
import { FamilyMemberSelectionStep } from "./components/FamilyMemberSelectionStep/FamilyMemberSelectionStep";

interface IGroupClassWizardProps {
  className?: string;
}

export const GroupClassWizard = ({ className }: IGroupClassWizardProps) => {
  const { selectedStudio, currentUser, isDesktopOrTablet } = useAppContext();
  const [hasAttemptedProceed, setHasAttemptedProceed] = useState(false);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);
  const [showPrepaymentDialog, setShowPrepaymentDialog] = useState(false);
  const navigate = useNavigate();

  // Handle Google OAuth return flow
  const { isLoadingProfile } = useGoogleAuthReturn();

  // Apply theme colors from studio preferences: the studio's Design Studio accent when
  // it has one, else its background colours, as before.
  useEffect(() => {
    applyStudioWizardAccent(selectedStudio);
  }, [selectedStudio]);

  const {
    currentStep,
    stepIndex,
    totalSteps,
    selection,
    progressPercentage,
    isTransitioning,
    availableClasses,
    classCategories,
    classesByCategory,
    availableInstructors,
    availableFamilyMembers,
    canProceedFromStep,
    isLoading,
    reservationId,
    isWaitlistBooking,
    classesLoading,
    instructorsLoading,
    familyMembersLoading,
    pendingInstructorAutoSkip,
    goToStep,
    nextStep,
    previousStep,
    selectClass,
    selectInstructor,
    selectDateTime,
    selectDateOnly,
    selectFamilyMember,
    createReservation,
    triggerAuthRedirect,
    resolveConfirmAction,
  } = useGroupClassWizard();

  const preReservationForm = usePreReservationFormForEvent(selection.eventId);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const formSubmissionRef = useRef<IPreReservationFormSubmission | null>(null);

  // Re-require the consent form whenever the reservation target changes (a different
  // class occurrence or a different family member), so a submission filled for one
  // person/booking is never silently reused for another.
  useEffect(() => {
    formSubmissionRef.current = null;
  }, [selection.eventId, selection.familyMember?.id]);

  const { isEntering, isExiting } = useWizardAnimations(isTransitioning);

  // Reset validation attempt flag when step changes
  useEffect(() => {
    setHasAttemptedProceed(false);
  }, [currentStep]);

  // Smooth scroll to top on step change
  useEffect(() => {
    const wizardContent = document.querySelector(`.${styles.wizardContent}`);
    if (wizardContent) {
      wizardContent.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [currentStep]);

  // Handle forward navigation attempt
  const handleNextStep = () => {
    if (!canProceedFromStep) {
      setHasAttemptedProceed(true);
      return;
    }

    // Special handling for confirmation step - create reservation
    if (currentStep === "confirmation") {
      // Check if user is unreliable
      if (currentUser?.isUnreliable) {
        showCustomConfirmDialog(
          <>
            <div>
              За съжаление в момента не можете да направите тази резервация през
              портала.
            </div>
            <br />
            <div>Моля свържете се с обекта за повече информация.</div>
          </>,
          () => callPhoneNumber(selectedStudio?.phoneNumber),
          () => {},
          "warning",
          "Свържи се с обекта",
          "Затвори",
        );
        return;
      }

      // Form first, then payment method, then reserve — see reservationConfirmUtility.
      const action = resolveConfirmAction(
        !!preReservationForm?.id,
        !!formSubmissionRef.current,
      );

      if (action === "fillForm") {
        setShowFormDialog(true);
        return;
      }

      if (action === "choosePayment") {
        setShowPrepaymentDialog(true);
        return;
      }

      createReservation(null, formSubmissionRef.current);
    } else {
      nextStep();
    }
  };

  const handlePrepaymentConfirm = (result: IPrepaymentDialogConfirmResult) => {
    setShowPrepaymentDialog(false);
    createReservation(
      result.subscriptionPlanId,
      formSubmissionRef.current,
      result.payWithMultisport,
    );
  };

  const handlePrepaymentCancel = () => {
    setShowPrepaymentDialog(false);
  };

  const handleFormAgree = (answers: string[]) => {
    if (preReservationForm?.id) {
      formSubmissionRef.current = {
        preReservationFormId: preReservationForm.id,
        answers,
      };
    }
    setShowFormDialog(false);
    handleNextStep();
  };

  const handleFormDisagree = () => {
    setShowFormDialog(false);
    getErrorToast(
      "Резервацията не може да бъде направена без да попълните формуляра.",
    );
  };

  // Show loading screen until studio data is loaded or while processing OAuth
  // Also show while waiting for instructors to load after a fast "next" click
  if (!selectedStudio || isLoadingProfile || pendingInstructorAutoSkip) {
    return <WizardLoadingScreen />;
  }

  // Validation message
  const validationMessage =
    hasAttemptedProceed && !canProceedFromStep
      ? getValidationMessage(currentStep, selection)
      : undefined;

  // Can navigate logic
  const canNavigateBack = stepIndex > 0 && currentStep !== "success";
  const canNavigateForward =
    stepIndex < totalSteps - 1 && currentStep !== "success";

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case "classes": {
        // Build a set of valid category IDs so classes with deleted categories
        // are treated as uncategorized (categoryId: null) in this wizard only
        const validCategoryIds = new Set(classCategories.map((c) => c.id));
        const resolvedCategoryId = (categoryId: number | null) =>
          categoryId !== null && validCategoryIds.has(categoryId)
            ? categoryId
            : null;

        // Transform classes to look like services for ServicesSelectionStep
        const classesAsServices = availableClasses.map((cls) => ({
          id: cls.className as unknown as number, // Use className as id for grouping
          name: cls.className,
          description: `${cls.upcomingInstancesCount} предстоящи ${cls.upcomingInstancesCount === 1 ? "класа" : "класа"}`,
          duration: 0,
          price: 0,
          minPrice: 0,
          maxPrice: 0,
          isActive: true,
          categoryId: resolvedCategoryId(cls.categoryId),
          sortOrder: 0,
        }));

        return (
          <ServicesSelectionStep
            services={classesAsServices}
            categories={classCategories}
            servicesByCategory={
              Object.fromEntries(
                Object.entries(classesByCategory).map(([key, classes]) => [
                  key,
                  classes.map((cls) => ({
                    id: cls.className as unknown as number,
                    name: cls.className,
                    description: `${cls.upcomingInstancesCount} предстоящи ${cls.upcomingInstancesCount === 1 ? "класа" : "класа"}`,
                    duration: 0,
                    price: 0,
                    minPrice: 0,
                    maxPrice: 0,
                    isActive: true,
                    categoryId: resolvedCategoryId(cls.categoryId),
                    sortOrder: 0,
                  })),
                ]),
              ) as Record<number | "uncategorized", any>
            }
            selectedService={
              selection.class
                ? {
                    id: selection.class.className as unknown as number,
                    name: selection.class.className,
                    description: "",
                    duration: 0,
                    price: 0,
                    minPrice: 0,
                    maxPrice: 0,
                    isActive: true,
                    categoryId: resolvedCategoryId(selection.class.categoryId),
                    sortOrder: 0,
                  }
                : null
            }
            onServiceSelect={(service) => {
              const selectedClass = availableClasses.find(
                (cls) => cls.className === (service.id as unknown as string),
              );
              if (selectedClass) {
                selectClass(selectedClass);
              }
            }}
            isLoading={classesLoading}
            title="Видове класове"
            subtitle="Изберете класа, който искате да резервирате"
            loadingText="Зареждане на класове..."
            emptyStateText="В момента няма налични групови занимания"
            isGroupClassMode={true}
            countLabelSingular="събитие"
            countLabelPlural="събития"
          />
        );
      }
      case "instructors":
        // Transform instructors to look like employees for EmployeesSelectionStep
        const instructorsAsEmployees = availableInstructors.map((inst) => ({
          id: inst.id,
          name: inst.name,
          role: "",
          isAvailable: true,
          priceModifier: 1,
          bookedSlots: [],
        }));

        return (
          <EmployeesSelectionStep
            employees={instructorsAsEmployees}
            selectedEmployee={
              selection.instructor
                ? {
                    id: selection.instructor.id,
                    name: selection.instructor.name,
                    role: "",
                    isAvailable: true,
                    priceModifier: 1,
                    bookedSlots: [],
                  }
                : null
            }
            onEmployeeSelect={(employee) => {
              const selectedInstructor = availableInstructors.find(
                (inst) => inst.id === employee.id,
              );
              if (selectedInstructor) {
                selectInstructor(selectedInstructor);
              }
            }}
            isLoading={instructorsLoading}
          />
        );
      case "datetime":
        return (
          <GroupClassDateTimeSelectionStep
            instructorId={selection.instructor?.id}
            instructorName={selection.instructor?.name}
            selectedClassName={selection.class?.className}
            selectedDate={selection.date}
            selectedTime={selection.time}
            selectedEventId={selection.eventId}
            onDateSelect={selectDateOnly}
            onTimeSelect={(
              time,
              eventId,
              actualInstructorName,
              actualInstructorId,
              durationMinutes,
              isWaitlistBooking,
              requirePrepayment,
              prepaymentLink,
              requireFormCompletion,
              formCompletionLink,
              allowOnlineReservations,
              prepaymentProvider,
              price,
              isMultisportAvailable,
            ) => {
              if (selection.date) {
                selectDateTime(
                  selection.date,
                  time,
                  eventId,
                  actualInstructorName,
                  actualInstructorId,
                  durationMinutes,
                  isWaitlistBooking,
                  requirePrepayment,
                  prepaymentLink,
                  requireFormCompletion,
                  formCompletionLink,
                  allowOnlineReservations,
                  prepaymentProvider,
                  price,
                  isMultisportAvailable,
                );
              }
            }}
            isLoading={isLoading}
          />
        );
      case "login":
        const wizardStateForLogin = {
          currentStep,
          stepIndex,
          totalSteps,
          isTransitioning,
          canNavigateBack: false,
          canNavigateForward: false,
          selection,
          isLoading,
          error: null,
          reservationId,
        };

        return (
          <GroupClassLoginStep
            onLoginSuccess={(user) => {
              if (!!user) {
                setIsLoginInProgress(true);
                setIsLoginInProgress(false);
                triggerAuthRedirect();
              }
            }}
            wizardState={wizardStateForLogin}
          />
        );
      case "familyMember":
        return (
          <FamilyMemberSelectionStep
            familyMembers={availableFamilyMembers}
            selectedFamilyMember={selection.familyMember || null}
            onFamilyMemberSelect={selectFamilyMember}
            isLoading={familyMembersLoading}
          />
        );
      case "confirmation":
        return (
          <GroupClassConfirmationStep
            selectedClass={selection.class}
            selectedInstructor={selection.instructor}
            selectedDate={selection.date}
            selectedTime={selection.time}
            durationMinutes={selection.durationMinutes}
            actualInstructorName={selection.actualInstructorName}
            actualInstructorId={selection.actualInstructorId}
            selectedFamilyMember={selection.familyMember}
            isLoading={isLoading}
            isWaitlistBooking={isWaitlistBooking}
            requirePrepayment={selection.requirePrepayment}
            prepaymentLink={selection.prepaymentLink}
            prepaymentProvider={selection.prepaymentProvider}
          />
        );
      case "success":
        return (
          <GroupClassSuccessStep
            selectedClass={selection.class}
            selectedInstructor={selection.instructor}
            actualInstructorName={selection.actualInstructorName}
            selectedDate={selection.date}
            selectedTime={selection.time}
            selectedFamilyMember={selection.familyMember}
            reservationId={reservationId || undefined}
            isWaitlistBooking={isWaitlistBooking}
            requirePrepayment={selection.requirePrepayment}
            prepaymentLink={selection.prepaymentLink}
            requireFormCompletion={selection.requireFormCompletion}
            formCompletionLink={selection.formCompletionLink}
            onBookAnother={() => {
              navigate(0);
            }}
            onGoToDashboard={() => {
              navigate(routePaths.portalMyReservations);
            }}
          />
        );
      default:
        return (
          <div className={styles.tempContent}>
            <p>Неизвестна стъпка: {currentStep}</p>
          </div>
        );
    }
  };

  return (
    <div className={classNames(styles.wizardContainer, className)}>
      <ProgressBar progress={progressPercentage} />
      <WizardHeader />

      <div className={styles.wizardContent}>
        <WizardStep
          isActive={true}
          isEntering={isEntering}
          isExiting={isExiting}
        >
          {renderStepContent()}
        </WizardStep>
      </div>

      {currentStep === "datetime" &&
        selection.allowOnlineReservations === false &&
        !!selection.time && (
          <div className={styles.contactStudio}>
            <p className={styles.contactStudio_message}>
              За резервация за този клас, моля свържете се директно с нас.
            </p>
            <DefaultButton
              onClick={() => callPhoneNumber(selectedStudio?.phoneNumber)}
              label="Свържи се с обекта"
              design="secondary"
              width="auto"
            />
          </div>
        )}

      <WizardNavigation
        currentStep={currentStep as any}
        canNavigateBack={canNavigateBack}
        canNavigateForward={canNavigateForward && canProceedFromStep}
        isLoading={isLoading || isLoginInProgress}
        validationMessage={validationMessage}
        onBack={previousStep}
        onForward={handleNextStep}
      />

      {/* Floating, draggable back control. The wizard drives its steps through
          browser history, so a plain history back steps through the wizard and
          exits it at the first step. */}
      <DraggableBackButton onBack={() => window.history.back()} />

      <PrepaymentDialog
        visible={showPrepaymentDialog}
        eligiblePlansEndpoint={
          selection.eventId && (selection.familyMember?.id ?? currentUser?.id)
            ? `${endpointsMetadata.portalCalendar.eligiblePlans}?eventId=${selection.eventId}&userId=${selection.familyMember?.id ?? currentUser?.id}`
            : undefined
        }
        eligiblePlansQueryKey={[
          endpointsMetadata.portalCalendar.eligiblePlans,
          selection.eventId,
          selection.familyMember?.id ?? currentUser?.id,
        ]}
        price={selection.price ?? 0}
        prepaymentProvider={selection.prepaymentProvider}
        isDesktopOrTablet={isDesktopOrTablet}
        isPrepaid={!!selection.requirePrepayment}
        allowMultisport={!!selection.isMultisportAvailable}
        onConfirm={handlePrepaymentConfirm}
        onCancel={handlePrepaymentCancel}
      />

      {preReservationForm?.id && (
        <PreReservationFormDialog
          visible={showFormDialog}
          formName={preReservationForm.name}
          templateText={preReservationForm.templateText}
          contextValues={buildClientFormContext(selectedStudio, currentUser, {
            eventName: selection.class?.className ?? "",
            price: formatFormPrice(selection.price),
          })}
          onAgree={handleFormAgree}
          onDisagree={handleFormDisagree}
        />
      )}
    </div>
  );
};

// Helper function to get validation message
function getValidationMessage(
  currentStep: string,
  selection: any,
): string | undefined {
  switch (currentStep) {
    case "classes":
      return "Моля изберете клас";
    case "instructors":
      return "Моля изберете инструктор";
    case "datetime":
      if (!selection.date) return "Моля изберете дата";
      if (!selection.time) return "Моля изберете час";
      return undefined;
    case "familyMember":
      return "Моля изберете член на семейството";
    default:
      return undefined;
  }
}
